import type BigNumber from 'bignumber.js';
import type { ReactNode } from 'react';

import type { SwidgeListOutput } from '@infinex/actions';
import type {
  AssetId,
  NftDisplayMetadataMap,
  PointType,
} from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type { Raffle, RafflePrize } from '@infinex/crate-config';
import type { GasActivity as GasActivityServer } from '@infinex/main-service-contract/v2/client/gas';
import type {
  AirdropAllocation,
  EscrowSendClaimDestinationType,
  EscrowSendCustodyBackend,
  EscrowSendIdentityType,
  EscrowSendStatus,
  PatronPurchaseTier,
  PlatformPatronPurchaseWave,
} from '@infinex/types';

export type BaseRowExplorerLink =
  | {
      chain: ChainKey;
      txnHash?: string;
    }
  | ReactNode;

// Like a TypeScript enum but "erasable" so works with `strip-types`
export const ActivityType = {
  SEND: 'SEND',
  SWAP: 'SWAP',
  MOVE: 'MOVE',
  BRIDGE: 'BRIDGE',
  PATRON_REDEMPTION: 'PATRON_REDEMPTION',
  PATRON_PURCHASE: 'PATRON_PURCHASE',
  SONAR_SALE_PURCHASE: 'SONAR_SALE_PURCHASE',
  AIRDROP: 'AIRDROP',
  AIRDROP_CLAIM: 'AIRDROP_CLAIM',
  CASHBACK_CLAIM: 'CASHBACK_CLAIM',
  PRIZE_DRAW_WIN: 'PRIZE_DRAW_WIN',
  SWIDGE: 'SWIDGE',
  EXTERNAL_APP_ACTION: 'EXTERNAL_APP_ACTION',
  GAS: 'GAS',
  INX_CLAIM: 'INX_CLAIM',
  INX_EARLY_UNLOCK: 'INX_EARLY_UNLOCK',
  INX_VOUCHER_REDEMPTION: 'INX_VOUCHER_REDEMPTION',
  PRIVATE_SEND: 'PRIVATE_SEND',
  PRIVATE_RECEIVE: 'PRIVATE_RECEIVE',
} as const;
export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

// Like a TypeScript enum but "erasable" so works with `strip-types`
export const ActivityStatus = {
  CREATED: 'Created',
  PROCESSING: 'Processing',
  SUCCESS: 'Success',
  ERROR: 'Error',
  REVIEW: 'Review',
} as const;
export type ActivityStatus =
  (typeof ActivityStatus)[keyof typeof ActivityStatus];

export interface BaseActivity {
  id: string;
  type: ActivityType;
  status: ActivityStatus;
  timestamp: Date;
}

export interface SwidgeActivity extends BaseActivity {
  type: typeof ActivityType.SWIDGE;
  fromAssetId?: AssetId;
  toAssetId?: AssetId;
  fromAssetAddress: string;
  toAssetAddress: string;
  fromAmount: string;
  quotedToAmount: string;
  fromChain: ChainKey;
  toChain: ChainKey;
  explorerLink: BaseRowExplorerLink;
  rawResponse: SwidgeListOutput['orders'][number];
}

export interface ExternalActionActivity extends BaseActivity {
  type: typeof ActivityType.EXTERNAL_APP_ACTION;
  action: 'deposit' | 'withdraw';
  protocol: string;
  chain: ChainKey;
  assetId?: AssetId;
  assetAddress: string;
  amount: string;
  amountUsd?: string;
  decimals: number;
  txnHash?: string;
  walletAddress: string;
}

// Generic withdrawal types (to avoid web-app specific dependencies)
export interface WithdrawalResponse {
  [key: string]: any;
}

export type AirdropAllocationWithKey = AirdropAllocation & {
  key: string;
  decimals: number;
  title: string;
};

export interface SendActivity extends BaseActivity {
  type: typeof ActivityType.SEND;
  toAddress: string;
  fromAddress: string;
  amount: BigNumber;
  decimals: number;
  amountUsd: number | null;
  assetId?: AssetId;
  assetAddress: string;
  chain: ChainKey;
  txnHash?: string;
  nftMetadata?: NftDisplayMetadataMap;
  rawResponse?: WithdrawalResponse;
  isMove?: boolean;
}

export interface PatronRedemptionActivity extends BaseActivity {
  type: typeof ActivityType.PATRON_REDEMPTION;
  assetValue: number;
  assetId: AssetId;
  quantity: number;
  tier: PatronPurchaseTier;
  wave: PlatformPatronPurchaseWave;
  chain: ChainKey;
  burnTxnHash?: string;
  claimTxnHash?: string;
}

export interface PatronPurchaseActivity extends BaseActivity {
  type: typeof ActivityType.PATRON_PURCHASE;
  assetValue: string;
  rateToUsd: number;
  assetId: AssetId;
  quantity: number;
  tier: PatronPurchaseTier;
  wave: PlatformPatronPurchaseWave;
  chain: ChainKey;
  transactionHash?: string;
}

export interface SonarSalePurchaseActivity extends BaseActivity {
  type: typeof ActivityType.SONAR_SALE_PURCHASE;
  decimalAmountUsdc: string;
  walletAddress: string;
  txHash?: string;
  chain: ChainKey;
}

export interface PrizeDrawWinActivity extends BaseActivity {
  type: typeof ActivityType.PRIZE_DRAW_WIN;
  prizeDraw: Omit<Raffle, 'drawRecords' | 'totalTickets'>;
  prize: RafflePrize;
  transactionHash?: string;
  ticketsHeld: number;
  position: number;
  fulfilledAt?: Date;
}

export interface AirdropActivity extends BaseActivity {
  type: typeof ActivityType.AIRDROP | typeof ActivityType.AIRDROP_CLAIM;
  amount: string;
  amountUsdFormatted?: string;
  chain?: ChainKey;
  assetId?: AssetId;
  pointType?: PointType;
  denomination?: string;
  txnHash?: string;
  decimals: number;
  reason?: string;
  tag?: string;
  title?: string;
  receiverAddress?: string;
}

export interface CashbackClaimActivity extends BaseActivity {
  type: typeof ActivityType.CASHBACK_CLAIM;
  amount: string;
  amountUi: string;
  amountUsdFormatted?: string;
  chain: ChainKey;
  assetAddress: string;
  decimals: number;
  txnHash?: string;
  isInReview: boolean;
  requestId: string;
}

export type GasActivity = {
  type: typeof ActivityType.GAS;
} & Omit<GasActivityServer, 'type' | 'status'> &
  BaseActivity;

/**
 * INX claim activity from Magna allocations.
 * Represents tokens claimed from patron vesting, team allocations, etc.
 */
export interface InxClaimActivity extends BaseActivity {
  type: typeof ActivityType.INX_CLAIM;
  /**
   * Amount of INX received human-readable decimal string like "6849.315068493150684932", not a raw integer.
   * DO NOT use with BigInt or any other functions that expect a raw integer.
   */
  decimalInxAmount: string;
  /** Allocation category (patron_vesting_1, team, etc.) */
  categoryKey: string;
  /** Wallet address that received the INX */
  walletAddress: string;
  /** Chain where the claim occurred (always ethereum for INX) */
  chain: ChainKey;
  /** On-chain transaction hash (present when completed) */
  txHash?: string;
  /** USD value at the time of claim */
  amountUsd?: string;
}

/**
 * INX early unlock activity from Sonar sale allocations.
 * User pays USDC to unlock INX tokens early.
 */
export interface InxEarlyUnlockActivity extends BaseActivity {
  type: typeof ActivityType.INX_EARLY_UNLOCK;
  /** Amount of INX received (decimal string, 18 decimals) */
  decimalInxAmount: string;
  /** Amount of USDC paid (decimal string, 6 decimals) */
  decimalPaymentAmount: string;
  /** Strike price per token in USD */
  strikePrice: string;
  /** Amount already prepaid during Sonar sale (decimal string) */
  decimalPrepaidAmount: string;
  /** Wallet address that received the INX */
  walletAddress: string;
  /** Chain where the unlock occurred (always ethereum for INX) */
  chain: ChainKey;
  /** On-chain transaction hash (present when completed) */
  txHash?: string;
  /** Treasury address that received the USDC payment */
  treasuryAddress: string;
  /** USD value of INX at redeem time */
  amountUsd?: string;
}

/**
 * INX voucher redemption activity from crate vouchers.
 * User pays USDC to redeem a voucher for INX tokens.
 */
export interface InxVoucherRedemptionActivity extends BaseActivity {
  type: typeof ActivityType.INX_VOUCHER_REDEMPTION;
  /** Amount of INX received (decimal string, 18 decimals) */
  decimalInxAmount: string;
  /** Amount of USDC paid (decimal string, 6 decimals) */
  decimalPaymentAmount: string;
  /** Strike price per token in USD */
  strikePrice: string;
  /** Ledger denomination ID for the voucher */
  denominationId: string;
  /** Wallet address that received the INX */
  walletAddress: string;
  /** Chain where the redemption occurred (always ethereum for INX) */
  chain: ChainKey;
  /** On-chain transaction hash (present when completed) */
  txHash?: string;
  /** Treasury address that received the USDC payment */
  treasuryAddress: string;
  /** USD value of INX at redeem time */
  amountUsd?: string;
}

/**
 * A private (escrow) send created by this user — by Infinex username,
 * X handle, or bearer claim link. `status` is the coarse feed status;
 * `escrowStatus` keeps the full backend state machine for badges and the
 * detail page. Amounts are raw base units.
 */
export interface PrivateSendActivity extends BaseActivity {
  type: typeof ActivityType.PRIVATE_SEND;
  escrowStatus: EscrowSendStatus;
  recipientIdentityType: EscrowSendIdentityType;
  recipientHandleAtSend: string | null;
  custodyBackend: EscrowSendCustodyBackend;
  chain: ChainKey;
  assetAddress: string;
  decimals: number;
  amount: BigNumber;
  settledAmount: BigNumber | null;
  expiresAt: Date | null;
}

/**
 * A private (escrow) send this user has claimed. Only fully-claimed sends
 * appear (in-flight claims live in the claim dialog). `amount` is what was
 * received (settled); `sentAmount` the sender's quoted amount — the delta
 * renders as network cost.
 */
export interface PrivateReceiveActivity extends BaseActivity {
  type: typeof ActivityType.PRIVATE_RECEIVE;
  custodyBackend: EscrowSendCustodyBackend;
  chain: ChainKey;
  assetAddress: string;
  decimals: number;
  amount: BigNumber;
  sentAmount: BigNumber;
  claimDestinationType: EscrowSendClaimDestinationType;
  claimedToAddress: string | null;
}

export type Activity =
  | SendActivity
  | AirdropActivity
  | CashbackClaimActivity
  | PatronRedemptionActivity
  | PatronPurchaseActivity
  | SonarSalePurchaseActivity
  | SwidgeActivity
  | ExternalActionActivity
  | PrizeDrawWinActivity
  | GasActivity
  | InxClaimActivity
  | InxEarlyUnlockActivity
  | InxVoucherRedemptionActivity
  | PrivateSendActivity
  | PrivateReceiveActivity;
